import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Download, Search } from 'lucide-react';
import { toast } from 'sonner';
import { formatEuro } from '../utils/money';
import { useUser } from '../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Compute [from, to] ISO YYYY-MM-DD range for the header period filter.
// Mirrors Pipeline.js getPeriodDateRange but returns strings for URL params.
function computePeriodRange(period, customDateRange) {
  const now = new Date();
  const iso = (d) => (d instanceof Date && !isNaN(d) ? d.toISOString().slice(0, 10) : null);
  let from, to;
  switch (period) {
    case 'week': {
      const dow = now.getDay(); const diff = dow === 0 ? 6 : dow - 1;
      from = new Date(now); from.setDate(now.getDate() - diff); from.setHours(0,0,0,0);
      to = new Date(from); to.setDate(from.getDate() + 6); break;
    }
    case 'month':
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to   = new Date(now.getFullYear(), now.getMonth() + 1, 0); break;
    case 'quarter': {
      const q = Math.floor(now.getMonth() / 3);
      from = new Date(now.getFullYear(), q * 3, 1);
      to   = new Date(now.getFullYear(), q * 3 + 3, 0); break;
    }
    case 'year':
      from = new Date(now.getFullYear(), 0, 1);
      to   = new Date(now.getFullYear(), 11, 31); break;
    case 'custom':
      from = customDateRange?.from; to = customDateRange?.to; break;
    default:
      return { from: null, to: null };
  }
  return { from: iso(from), to: iso(to) };
}

const HDR = 'bg-[#4472C4] text-white font-semibold text-xs px-2 py-2 border border-slate-300 text-center';
const CELL = 'text-xs px-2 py-1 border border-slate-200 align-top';
const ESTADO_BG = {
  'Adjudicada': 'bg-[#70AD47] text-white',
  'Adjudicada a otro': 'bg-[#ED7D31] text-white',
  'Pendiente de Adjudicación': 'bg-[#FFC000] text-slate-900',
  'TOTAL': 'bg-[#4472C4] text-white',
};

const fmtPct = (v) => (v === null || v === undefined || v === '') ? '-' : `${Number(v).toFixed(2).replace('.', ',')}%`;
const fmtDate = (v) => { if (!v) return ''; try { return new Date(v).toLocaleDateString('es-ES'); } catch { return v; } };
const truncate = (s, n = 60) => (!s ? '' : (s.length > n ? s.slice(0, n) + '…' : s));

export default function OpportunityValoradas({
  clientTypeFilter = ['typsa_gg', 'typsa_colab', 'privada', 'publica'],
  ownerFilter = 'all',
  periodFilter = 'all',
  customDateRange = null,
}) {
  const { user } = useUser();
  const [tab, setTab] = useState('detalle');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [includeAll, setIncludeAll] = useState(false);
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('todas'); // todas | en_proceso | adjudicadas_tds | adjudicadas_otros
  const [loading, setLoading] = useState(true);

  // Build backend params from Pipeline header filters.
  const backendParams = useMemo(() => {
    const p = { include_all: includeAll ? 1 : 0 };
    // client types — only send when it's a strict subset of the 4 keys.
    if (clientTypeFilter && clientTypeFilter.length > 0 && clientTypeFilter.length < 4) {
      p.client_types = clientTypeFilter.join(',');
    }
    // owner (name-based)
    if (ownerFilter === 'me' && user?.name) p.owner = user.name;
    else if (ownerFilter && ownerFilter !== 'all' && ownerFilter !== 'me' && ownerFilter !== 'unassigned') p.owner = ownerFilter;
    // period → date range
    if (periodFilter && periodFilter !== 'all') {
      const { from, to } = computePeriodRange(periodFilter, customDateRange);
      if (from) p.date_from = from;
      if (to)   p.date_to   = to;
    }
    // estado (Valoradas own segmented control)
    if (estado && estado !== 'todas') p.estado = estado;
    return p;
  }, [includeAll, clientTypeFilter, ownerFilter, periodFilter, customDateRange, user?.name, estado]);

  useEffect(() => { (async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/valoradas/list`, { params: backendParams }),
        axios.get(`${API}/valoradas/summary`, { params: backendParams }),
      ]);
      setRows(l.data.rows || []);
      setSummary(s.data);
    } catch (e) { toast.error('No se pudieron cargar las Valoradas'); }
    finally { setLoading(false); }
  })(); }, [backendParams]);

  const filtered = search
    ? rows.filter(r => (r.titulo || '').toLowerCase().includes(search.toLowerCase()) ||
                       (r.cliente_final || '').toLowerCase().includes(search.toLowerCase()))
    : rows;

  const handleExport = async () => {
    try {
      const r = await axios.get(`${API}/valoradas/export.xlsx`, {
        params: backendParams,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `ESTADO_OFERTAS_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error al descargar el Excel'); }
  };

  return (
    <div className="p-3 space-y-3 max-w-full min-w-0 overflow-hidden" data-testid="valoradas-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Tabs value={tab} onValueChange={setTab} className="w-auto">
          <TabsList>
            <TabsTrigger value="detalle" data-testid="valoradas-tab-detalle">ESTADO OFERTAS</TabsTrigger>
            <TabsTrigger value="resumen" data-testid="valoradas-tab-resumen">RESUMEN</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Estado segmented control — Adjudicadas / En proceso */}
          <div className="flex items-center gap-1 p-0.5 rounded-md border bg-muted/30" data-testid="valoradas-estado-filter">
            {[
              ['todas', 'Todas', 'text-slate-700'],
              ['en_proceso', 'En proceso', 'bg-[#FFC000] text-slate-900'],
              ['adjudicadas_tds', 'Adjudicadas TDS', 'bg-[#70AD47] text-white'],
              ['adjudicadas_otros', 'Adjudicadas a otros', 'bg-[#ED7D31] text-white'],
            ].map(([key, label, activeCls]) => (
              <button
                key={key}
                onClick={() => setEstado(key)}
                className={`px-2 py-1 rounded text-[11px] font-medium transition-all ${
                  estado === key ? activeCls + ' shadow-sm' : 'text-muted-foreground hover:bg-muted'
                }`}
                data-testid={`valoradas-estado-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input type="checkbox" checked={includeAll} onChange={(e) => setIncludeAll(e.target.checked)} data-testid="include-all-toggle" />
            Incluir Detectadas / Propuestas / Descartadas
          </label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Título o cliente..." className="pl-7 h-8 w-56" data-testid="valoradas-search" />
          </div>
          <Button size="sm" onClick={handleExport} data-testid="valoradas-export-btn">
            <Download className="w-4 h-4 mr-2" />Descargar Excel
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsContent value="detalle" className="mt-0 min-w-0">
          <Card className="min-w-0 max-w-full">
            {/* max-h + overflow-auto keeps the horizontal scrollbar visible at the
                bottom of the viewport even when the table has many rows. The
                -240 offset covers the page title (72px) + Pipeline tabs row
                (48px) + Valoradas toolbar row (48px) + margins (~70px). */}
            <CardContent className="p-0 overflow-auto max-w-full max-h-[calc(100vh-240px)]">
            {loading ? <div className="p-6 text-sm">Cargando...</div> :
            <table className="border-collapse w-max" data-testid="valoradas-table">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className={HDR} rowSpan={2}>Título</th>
                  <th className={HDR} rowSpan={2}>Descripción</th>
                  <th className={HDR} rowSpan={2}>Cliente final</th>
                  <th className="bg-[#8FAADC] text-white font-semibold text-xs px-2 py-1 border border-slate-300 text-center" colSpan={5}>DATOS ECONÓMICOS (SIN IVA)</th>
                  <th className={HDR} rowSpan={2}>Fecha de Entrega</th>
                  <th className={HDR} rowSpan={2}>Estado</th>
                  <th className={HDR} rowSpan={2}>Tipo Cliente</th>
                  <th className={HDR} rowSpan={2}>Comentario</th>
                  <th className={HDR} rowSpan={2}>Relevancia</th>
                  <th className={HDR} rowSpan={2}>Probabilidades</th>
                  <th className={HDR} rowSpan={2}>Licitador</th>
                  <th className="w-2 bg-slate-100 border border-slate-300" rowSpan={2}></th>
                  <th className={HDR} rowSpan={2}>ADJUDICATARIO FINAL</th>
                  <th className={HDR} rowSpan={2}>IMPORTE ADJUDICACIÓN</th>
                  <th className={HDR} rowSpan={2}>BAJA EMPRESA ADJUDICATARIA</th>
                  <th className={HDR} rowSpan={2}>BAJA GRUPO TYPSA-TDS</th>
                  <th className={HDR} rowSpan={2}>POSICIÓN GRUPO TYPSA-TDS</th>
                  <th className={HDR} rowSpan={2}>Nº DE LICITADORES</th>
                  <th className={HDR} rowSpan={2}>RESPONSABLE TDS DE LA OFERTA</th>
                </tr>
                <tr>
                  <th className={HDR}>Precio Base</th>
                  <th className={HDR}>Importe ofertado</th>
                  <th className={HDR}>Baja</th>
                  <th className={HDR}>Duración Contrato</th>
                  <th className={HDR}>Importe TDS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`valoradas-row-${r.id}`}>
                    <td className={CELL}>{r.titulo}</td>
                    <td className={CELL} title={r.descripcion}>{truncate(r.descripcion, 80)}</td>
                    <td className={CELL}>{r.cliente_final}</td>
                    <td className={CELL + ' text-right'}>{formatEuro(r.precio_base)}</td>
                    <td className={CELL + ' text-right'}>{formatEuro(r.importe_ofertado)}</td>
                    <td className={CELL}>{fmtPct(r.baja)}</td>
                    <td className={CELL}>{r.duracion_contrato}</td>
                    <td className={CELL + ' text-right font-semibold text-green-700'}>{formatEuro(r.importe_tds)}</td>
                    <td className={CELL}>{fmtDate(r.fecha_entrega)}</td>
                    <td className={CELL}>
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${ESTADO_BG[r.estado] || 'bg-slate-200'}`}>{r.estado}</span>
                      {r.stage_detail && <div className="text-[10px] text-muted-foreground mt-0.5">{r.stage_detail}</div>}
                    </td>
                    <td className={CELL}>{r.tipo_cliente}</td>
                    <td className={CELL} title={r.comentario}>{truncate(r.comentario, 80)}</td>
                    <td className={CELL}>{r.relevancia}</td>
                    <td className={CELL}>{r.probabilidades}</td>
                    <td className={CELL}>{r.licitador}</td>
                    <td className="bg-slate-50 border border-slate-200"></td>
                    <td className={CELL}>{r.adjudicatario_final}</td>
                    <td className={CELL + ' text-right'}>{formatEuro(r.importe_adjudicacion)}</td>
                    <td className={CELL}>{fmtPct(r.baja_empresa_adjudicataria)}</td>
                    <td className={CELL}>{fmtPct(r.baja_grupo_typsa_tds)}</td>
                    <td className={CELL}>{r.posicion_grupo_typsa_tds ?? ''}</td>
                    <td className={CELL}>{r.num_licitadores ?? ''}</td>
                    <td className={CELL}>{r.responsable_tds}</td>
                  </tr>
                ))}
              </tbody>
            </table>}
            {!loading && filtered.length === 0 && (<div className="p-6 text-sm text-muted-foreground text-center">Sin resultados</div>)}
          </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resumen" className="mt-0 space-y-4">
          {summary ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="valoradas-kpis">
                {[
                  ['Nº OFERTAS TOTALES', summary.kpis.total],
                  ['ADJUDICADAS', summary.kpis.adjudicadas],
                  ['TASA DE ÉXITO (s/ resueltas)', summary.kpis.tasa_exito_pct != null ? `${summary.kpis.tasa_exito_pct}%` : '-'],
                  ['IMPORTE GANADO (TDS)', formatEuro(summary.kpis.importe_ganado_tds)],
                  ['PIPELINE PENDIENTE (TDS)', formatEuro(summary.kpis.pipeline_pendiente_tds)],
                ].map(([k, v]) => (
                  <Card key={k} className="overflow-hidden">
                    <div className="bg-[#4472C4] text-white text-[10px] font-semibold px-2 py-1 text-center uppercase tracking-wide">{k}</div>
                    <div className="p-3 text-lg font-bold text-center">{v}</div>
                  </Card>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Nº DE OFERTAS</CardTitle></CardHeader>
                  <CardContent className="p-0"><PivotTable pivot={summary.counts_by_period} estados={summary.estados} periods={summary.periods} /></CardContent>
                </Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">IMPORTE TDS (€)</CardTitle></CardHeader>
                  <CardContent className="p-0"><PivotTable pivot={summary.amounts_by_period} estados={summary.estados} periods={summary.periods} isCurrency /></CardContent>
                </Card>
              </div>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Tendencia y acumulado</CardTitle></CardHeader>
                <CardContent className="p-0"><table className="min-w-full text-xs">
                  <thead><tr><th className={HDR}>Periodo</th><th className={HDR}>Imp. adjudicado TDS</th><th className={HDR}>Acumulado</th><th className={HDR}>Tasa adjudicación</th></tr></thead>
                  <tbody>{summary.trend.map(t => (
                    <tr key={t.period}><td className={CELL}>{t.period}</td><td className={CELL + ' text-right'}>{formatEuro(t.won_amount)}</td><td className={CELL + ' text-right'}>{formatEuro(t.cumulative)}</td><td className={CELL}>{t.rate_pct != null ? `${t.rate_pct}%` : '-'}</td></tr>
                  ))}</tbody>
                </table></CardContent>
              </Card>
              <p className="text-xs text-muted-foreground italic">Descarga el Excel para ver los gráficos nativos (barras apiladas, comparativa por periodo y dona de cartera).</p>
            </>
          ) : <div className="p-6 text-sm">Cargando...</div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const PivotTable = ({ pivot, estados, periods, isCurrency }) => (
  <table className="min-w-full text-xs">
    <thead><tr>
      <th className={HDR}></th>
      {periods.map(p => (<th key={p} className={HDR}>{p}</th>))}
      <th className={HDR}>TOTAL</th>
    </tr></thead>
    <tbody>
      {estados.map(e => {
        const tot = periods.reduce((a, p) => a + (pivot[e][p] || 0), 0);
        return (<tr key={e}>
          <td className={`${CELL} ${ESTADO_BG[e]} font-semibold`}>{e}</td>
          {periods.map(p => (<td key={p} className={CELL + ' text-right'}>{isCurrency ? formatEuro(pivot[e][p]) : pivot[e][p]}</td>))}
          <td className={CELL + ' text-right font-semibold'}>{isCurrency ? formatEuro(tot) : tot}</td>
        </tr>);
      })}
      <tr>
        <td className={`${CELL} ${ESTADO_BG.TOTAL} font-semibold`}>TOTAL</td>
        {periods.map(p => { const t = estados.reduce((a, e) => a + (pivot[e][p] || 0), 0); return (<td key={p} className={CELL + ' text-right font-bold'}>{isCurrency ? formatEuro(t) : t}</td>); })}
        <td className={CELL + ' text-right font-bold'}>{isCurrency ? formatEuro(estados.reduce((a, e) => a + Object.values(pivot[e]).reduce((x, y) => x + y, 0), 0)) : estados.reduce((a, e) => a + Object.values(pivot[e]).reduce((x, y) => x + y, 0), 0)}</td>
      </tr>
    </tbody>
  </table>
);
